import { useMemo } from "react";

/**
 * Read-only collapsible rate-table preview for the Found state. Renders
 * the normalized `rates` payload from the matcher as a compact age-band
 * grid. Tier dimensions (EE / EE+Spouse / etc.) aren't in the library —
 * those are derived from family composition at confirm time, so this
 * preview shows age bands × (tobacco/non-tobacco when present).
 */

interface RateEntry {
  age: number;
  ageRange: string;
  rate: number;
  tobacco?: boolean;
}

interface Props {
  // Tolerate `unknown[]` so we can defensively re-normalize entries that
  // were persisted before the server-side `normalizeRates()` fix landed
  // (raw W&B shape: `AgeFrom`, `AgeTo`, `Premium`, `AgeRange`). New data
  // arrives in the normalized `RateEntry` shape; this guard handles both.
  rates: Array<RateEntry | Record<string, unknown>> | null | undefined;
}

/**
 * Best-effort coercion of a single row from either the canonical
 * `RateEntry` shape (`{ age, ageRange, rate, tobacco? }`) or the raw
 * Word & Brown shape (`{ AgeFrom, AgeTo, Premium, AgeRange }`). Returns
 * null when neither shape carries a usable rate.
 */
function coerceRow(raw: unknown): RateEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const ageDirect =
    typeof row.age === "number"
      ? row.age
      : typeof row.age === "string"
        ? Number(row.age)
        : NaN;
  const rateDirect =
    typeof row.rate === "number"
      ? row.rate
      : typeof row.rate === "string"
        ? Number(row.rate)
        : NaN;
  const ageFrom =
    typeof row.AgeFrom === "number"
      ? row.AgeFrom
      : typeof row.AgeFrom === "string"
        ? Number(row.AgeFrom)
        : NaN;
  const premium =
    typeof row.Premium === "number"
      ? row.Premium
      : typeof row.Premium === "string"
        ? Number(row.Premium)
        : NaN;
  const age = Number.isFinite(ageDirect) ? ageDirect : ageFrom;
  const rate = Number.isFinite(rateDirect) ? rateDirect : premium;
  const ageRange =
    typeof row.ageRange === "string"
      ? row.ageRange
      : typeof row.AgeRange === "string"
        ? row.AgeRange
        : "";
  if (!Number.isFinite(age) || !Number.isFinite(rate)) return null;
  return {
    age,
    ageRange: ageRange || String(age),
    rate,
    tobacco: row.tobacco === true ? true : undefined,
  };
}

export function MatchRateTablePreview({ rates }: Props) {
  const { bands, hasTobacco, rateByBandTobacco } = useMemo(() => {
    if (!rates || rates.length === 0) {
      return {
        bands: [] as Array<{ label: string; sortKey: number }>,
        hasTobacco: false,
        rateByBandTobacco: new Map<string, number>(),
      };
    }
    const normalized = rates
      .map(coerceRow)
      .filter((r): r is RateEntry => r !== null);
    const labelToAge = new Map<string, number>();
    let hasTobacco = false;
    const map = new Map<string, number>();
    for (const r of normalized) {
      const label = r.ageRange || String(r.age);
      if (!labelToAge.has(label)) labelToAge.set(label, r.age);
      const t = r.tobacco === true;
      if (t) hasTobacco = true;
      map.set(`${label}:${t ? "y" : "n"}`, r.rate);
    }
    const bands = Array.from(labelToAge.entries())
      .map(([label, sortKey]) => ({ label, sortKey }))
      .sort((a, b) => a.sortKey - b.sortKey);
    return { bands, hasTobacco, rateByBandTobacco: map };
  }, [rates]);

  if (!rates || rates.length === 0) {
    return (
      <p className="px-1 py-2 text-[12px] text-stone-400">
        Rates will appear once the library match is confirmed.
      </p>
    );
  }

  const fmt = (n: number | undefined) =>
    n == null
      ? "—"
      : `$${n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  return (
    <div className="overflow-hidden rounded-md border border-stone-200 bg-white">
      <div className="max-h-[300px] overflow-y-auto">
        <table className="w-full text-[12px] tabular-nums">
          <thead className="sticky top-0 z-10 bg-stone-50 text-[10.5px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="border-b border-stone-200 px-3 py-2 text-left font-medium">
                Age
              </th>
              <th className="border-b border-stone-200 px-3 py-2 text-right font-medium">
                {hasTobacco ? "Non-tobacco" : "Monthly rate"}
              </th>
              {hasTobacco && (
                <th className="border-b border-stone-200 px-3 py-2 text-right font-medium">
                  Tobacco
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {bands.map(({ label }, i) => (
              <tr
                key={label}
                className={i % 2 === 0 ? "bg-white" : "bg-stone-50/40"}
              >
                <td className="px-3 py-1.5 font-mono text-stone-600">
                  {label}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-stone-900">
                  {fmt(rateByBandTobacco.get(`${label}:n`))}
                </td>
                {hasTobacco && (
                  <td className="px-3 py-1.5 text-right font-mono text-stone-900">
                    {fmt(rateByBandTobacco.get(`${label}:y`))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
